import { useState, type FormEvent } from 'react';
import { runWrite } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Empty, Kbd, Modal } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { MerchantPicker, SiteSelect } from './pickers.tsx';
import { PARAMETER_TYPES, type DeeplinkParameter, type Named, type NetworkDeeplinks, type ParameterLevel, type ParameterType, type FillSource } from './types.ts';

const LEVEL_HELP: Record<ParameterLevel, string> = {
  affiliate_network: 'one value for every link',
  site: 'one value per site (blank site = default)',
  global: 'one value per merchant, all countries',
  global_country: 'one value per merchant and country',
  shop: 'one value per merchant on one site',
  link: 'filled with the coupon’s deep URL',
  fallback_url: 'filled with the merchant’s fallback URL',
};

export function DeeplinkParameters({ data }: { data: NetworkDeeplinks }) {
  const toast = useToast();
  const [cursor, setCursor] = useState(0);
  const [editing, setEditing] = useState<DeeplinkParameter | 'new' | null>(null);
  const [deleting, setDeleting] = useState<DeeplinkParameter | null>(null);
  const params = data.parameters;
  const move = (param: DeeplinkParameter | undefined, direction: 'up' | 'down') => {
    if (!param || !data.canManage) return;
    void runWrite(toast, 'admin.deeplinks.moveParameter', { id: param.id, direction }, `${param.parameter} moved ${direction}`).then(() => setCursor((c) => step(c, direction === 'up' ? -1 : 1, params.length)));
  };
  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, params.length)),
      k: () => setCursor((c) => step(c, -1, params.length)),
      'shift+k': () => move(params[cursor], 'up'),
      'shift+j': () => move(params[cursor], 'down'),
      e: () => data.canManage && params[cursor] && setEditing(params[cursor]!),
      n: () => data.canManage && setEditing('new'),
    },
    editing === null && deleting === null,
  );

  return (
    <div>
      <p className="muted small">
        The link builder joins URL parts with “/” and adds query parameters in this order. <Kbd>j</Kbd>/<Kbd>k</Kbd> select · <Kbd>shift+j</Kbd>/<Kbd>shift+k</Kbd> move · <Kbd>e</Kbd> edit · <Kbd>n</Kbd> new parameter
      </p>
      {params.length === 0 ? <Empty>No parameters yet. Add them here, or read them from sample links (“Read from links”).</Empty> : null}
      <div className="card">
        {params.map((param, index) => (
          <ParameterBlock
            key={param.id}
            data={data}
            param={param}
            active={index === cursor}
            first={index === 0}
            last={index === params.length - 1}
            onSelect={() => setCursor(index)}
            onEdit={() => setEditing(param)}
            onDelete={() => setDeleting(param)}
            onMove={(direction) => move(param, direction)}
          />
        ))}
        {data.canManage ? (
          <button type="button" className="btn" onClick={() => setEditing('new')}>
            Add parameter
          </button>
        ) : (
          <p className="muted small">Parameters belong to the platform operator. You can set values for your own sites.</p>
        )}
      </div>
      {editing ? <ParameterForm data={data} param={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {deleting ? (
        <Modal
          title={`Delete ${deleting.parameter}?`}
          onClose={() => setDeleting(null)}
          actions={
            <>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  await runWrite<{ deletedValues: number }>(toast, 'admin.deeplinks.deleteParameter', { id: deleting.id }, (r) => `${deleting.parameter} deleted with ${r.deletedValues} value(s)`);
                  setDeleting(null);
                }}
              >
                Delete parameter and {deleting.values} value(s)
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setDeleting(null)}>
                Cancel
              </button>
            </>
          }
        >
          <p>Links built after this no longer carry {deleting.parameter}. Existing links keep their URLs until they are regenerated.</p>
        </Modal>
      ) : null}
    </div>
  );
}

function ParameterBlock({
  data,
  param,
  active,
  first,
  last,
  onSelect,
  onEdit,
  onDelete,
  onMove,
}: {
  data: NetworkDeeplinks;
  param: DeeplinkParameter;
  active: boolean;
  first: boolean;
  last: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (direction: 'up' | 'down') => void;
}) {
  const values = data.values.filter((v) => v.parameterId === param.id);
  const takesValues = !['link', 'fallback_url'].includes(param.level) && param.parameterType !== 'click_ref';
  return (
    <div className={`aff-param ${active ? 'cursor' : ''}`} onClick={onSelect} data-parameter={param.parameter}>
      <div className="aff-param-head">
        <div>
          <span className="muted">{param.position}.</span> <span className="mono strong">{param.parameter}</span> <span className="badge badge-none">{param.parameterType}</span>{' '}
          <span title={LEVEL_HELP[param.level]}>{param.levelLabel}</span>
          {param.isOptional ? <span className="muted"> · optional</span> : null}
          {param.fromProgram ? (
            <>
              {' '}
              <span className="badge badge-ok" title="Filled with each program’s network id; no value per merchant needed">from program</span>
            </>
          ) : null}
          {param.fillFrom ? (
            <>
              {' '}
              <span className="badge badge-ok" title="Filled from each site’s ad space or account on the network; no value per site needed">
                {param.fillFrom === 'ad_space' ? 'from ad space' : 'from account'}
              </span>
            </>
          ) : null}
          {param.fallbackValue ? (
            <span className="muted">
              {' '}
              · default <span className="mono">{param.fallbackValue}</span>
            </span>
          ) : null}
        </div>
        {data.canManage ? (
          <div className="aff-inline">
            <button type="button" className="btn btn-ghost btn-small" disabled={first} onClick={() => onMove('up')} aria-label={`Move ${param.parameter} up`}>
              ↑
            </button>
            <button type="button" className="btn btn-ghost btn-small" disabled={last} onClick={() => onMove('down')} aria-label={`Move ${param.parameter} down`}>
              ↓
            </button>
            <button type="button" className="btn btn-ghost btn-small" onClick={onEdit}>
              Edit
            </button>
            <button type="button" className="btn btn-danger-ghost btn-small" onClick={onDelete}>
              Delete
            </button>
          </div>
        ) : null}
      </div>
      {takesValues ? (
        <div className="aff-values">
          {param.fromProgram ? <p className="muted small" style={{ margin: 0 }}>Each merchant’s program id is used automatically. A value here overrides it for one merchant.</p> : null}
          {param.fillFrom ? (
            <p className="muted small" style={{ margin: 0 }}>
              Each site’s {param.fillFrom === 'ad_space' ? 'ad space id' : 'account id'} is used automatically (Network accounts). A value here overrides it.
            </p>
          ) : null}
          {values.map((v) => (
            <ValueRow key={v.id} value={v} />
          ))}
          <NewValue data={data} param={param} hasNetworkValue={values.some((v) => v.scope.label === 'All')} />
        </div>
      ) : (
        <p className="muted small" style={{ margin: '4px 0 0' }}>
          {param.parameterType === 'click_ref' ? 'Filled with the click id at clickout.' : `Value: ${LEVEL_HELP[param.level]}.`}
        </p>
      )}
    </div>
  );
}

function ValueRow({ value }: { value: NetworkDeeplinks['values'][number] }) {
  const toast = useToast();
  const [draft, setDraft] = useState(value.value);
  const changed = draft.trim() !== value.value && draft.trim() !== '';
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!changed) return;
    void runWrite(toast, 'admin.deeplinks.saveValue', valueInput(value), `${value.scope.label}: saved`);
  };
  function valueInput(v: typeof value) {
    return {
      parameterId: v.parameterId,
      siteRef: v.scope.site?.ref ?? null,
      merchantRef: v.scope.merchant?.ref ?? null,
      masterRef: v.scope.master?.ref ?? null,
      country: v.scope.country,
      value: draft.trim(),
    };
  }
  return (
    <form className="aff-inline" onSubmit={save}>
      <span style={{ width: 220 }} className="truncate" title={value.scope.label}>
        {value.scope.label}
      </span>
      <input className="mono" value={draft} readOnly={!value.editable} onChange={(e) => setDraft(e.target.value)} aria-label={`Value for ${value.scope.label}`} />
      {value.editable ? (
        <>
          <button type="submit" className="btn btn-small" disabled={!changed}>
            Save
          </button>
          <button type="button" className="btn btn-ghost btn-small" onClick={() => runWrite(toast, 'admin.deeplinks.deleteValue', { id: value.id }, `${value.scope.label}: value removed`)} aria-label={`Remove value for ${value.scope.label}`}>
            ×
          </button>
        </>
      ) : null}
    </form>
  );
}

function NewValue({ data, param, hasNetworkValue }: { data: NetworkDeeplinks; param: DeeplinkParameter; hasNetworkValue: boolean }) {
  const toast = useToast();
  const manageableSites: Named[] = data.sites.filter((s) => s.canManage);
  const [siteRef, setSiteRef] = useState(manageableSites[0]?.ref ?? '');
  const [merchant, setMerchant] = useState<Named | null>(null);
  const [value, setValue] = useState('');
  const platformOnly = ['affiliate_network', 'global', 'global_country'].includes(param.level);
  if (platformOnly && !data.canManage) return null;
  if (param.level === 'affiliate_network' && hasNetworkValue) return null;
  if (['site', 'shop'].includes(param.level) && manageableSites.length === 0) return null;
  const needsMerchant = ['shop', 'global', 'global_country'].includes(param.level);
  const needsSite = ['site', 'shop'].includes(param.level);
  const ready = value.trim() !== '' && (!needsMerchant || merchant !== null) && (param.level !== 'shop' || siteRef !== '');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    const done = await runWrite(
      toast,
      'admin.deeplinks.saveValue',
      { parameterId: param.id, siteRef: needsSite ? siteRef || null : null, merchantRef: merchant?.ref ?? null, value: value.trim() },
      `${param.parameter}: value saved`,
    );
    if (done) setValue('');
  };
  return (
    <form className="aff-inline" onSubmit={submit} aria-label={`Add value for ${param.parameter}`}>
      {needsSite ? (
        <SiteSelect sites={manageableSites} value={siteRef} onChange={setSiteRef} label="" allLabel={param.level === 'site' && data.canManage ? 'All sites (default)' : undefined} />
      ) : null}
      {needsMerchant ? <MerchantPicker value={merchant} onChange={setMerchant} label="" /> : null}
      <input className="mono" placeholder={`New value (${param.levelLabel.toLowerCase()})`} value={value} onChange={(e) => setValue(e.target.value)} aria-label={`New value for ${param.parameter}`} />
      <button type="submit" className="btn btn-small" disabled={!ready}>
        Add
      </button>
    </form>
  );
}

function ParameterForm({ data, param, onClose }: { data: NetworkDeeplinks; param: DeeplinkParameter | null; onClose: () => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState({
    parameter: param?.parameter ?? '',
    parameterType: param?.parameterType ?? ('query' as ParameterType),
    level: param?.level ?? ('affiliate_network' as ParameterLevel),
    isOptional: param?.isOptional ?? false,
    fallbackValue: param?.fallbackValue ?? '',
    fromProgram: param?.fromProgram ?? false,
    fillFrom: param?.fillFrom ?? (null as FillSource | null),
  });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const done = await runWrite(
      toast,
      'admin.deeplinks.saveParameter',
      { networkRef: data.network.ref, ...(param ? { id: param.id } : {}), ...draft, fallbackValue: draft.fallbackValue || null },
      `${draft.parameter} saved`,
    );
    if (done) onClose();
  };
  return (
    <Modal
      title={param ? `Edit ${param.parameter}` : 'New parameter'}
      onClose={onClose}
      actions={
        <>
          <button type="submit" form="parameter-form" className="btn btn-primary">
            Save
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <form id="parameter-form" className="form-grid" onSubmit={submit}>
        <label>
          Name
          <input required className="mono" value={draft.parameter} onChange={(e) => setDraft({ ...draft, parameter: e.target.value })} autoFocus />
        </label>
        <label>
          Type
          <select value={draft.parameterType} onChange={(e) => setDraft({ ...draft, parameterType: e.target.value as ParameterType })}>
            {PARAMETER_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="span-2">
          Level
          <select value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value as ParameterLevel })}>
            {data.levels.map((l) => (
              <option key={l.level} value={l.level}>
                {l.label} — {LEVEL_HELP[l.level]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Default value <small className="muted">used when no value matches</small>
          <input className="mono" value={draft.fallbackValue} onChange={(e) => setDraft({ ...draft, fallbackValue: e.target.value })} />
        </label>
        <label className="inline-check" style={{ alignSelf: 'end' }}>
          <input type="checkbox" checked={draft.isOptional} onChange={(e) => setDraft({ ...draft, isOptional: e.target.checked })} /> Optional (left out when empty)
        </label>
        <label className="inline-check span-2">
          <input type="checkbox" checked={draft.fromProgram} onChange={(e) => setDraft({ ...draft, fromProgram: e.target.checked, fillFrom: e.target.checked ? null : draft.fillFrom })} /> Merchant / program id: fill from the program’s network id (no value per merchant)
        </label>
        <label className="span-2">
          Site id <small className="muted">fill from each site’s ad space or account on this network</small>
          <select
            value={draft.fillFrom ?? ''}
            onChange={(e) => setDraft({ ...draft, fillFrom: (e.target.value || null) as FillSource | null, fromProgram: e.target.value ? false : draft.fromProgram })}
          >
            <option value="">Not filled automatically</option>
            <option value="ad_space">The site’s ad space id (CJ PID, Webgains campaign, digidip project, …)</option>
            <option value="account">The account id (Awin publisher id, …)</option>
          </select>
        </label>
      </form>
    </Modal>
  );
}
